import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Loader2 } from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext.jsx";
import { USE_API } from "./Campaignutils.jsx";
import MultiSelectDropdown from "../Common/MultiSelectDropdown.jsx";


// Location can arrive as a string (dummy data) or an object ({ _id | id, name }).
// Always work with { id, name } inside the modals.
const toOpt = (l) =>
  typeof l === "string"
    ? { id: l, name: l }
    : { id: l._id || l.id, name: l.name || l.locationName || "" };

// ── Shared UI ────────────────────────────────────────────────────────────────
export const inputCls = (err) =>
  `w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
    err ? "border-red-400 bg-red-50" : "border-gray-300 bg-white"
  }`;

// Campaign statuses: active | yet to start | paused | expired
// Asset statuses:    published | not published (| expired, shown when the campaign is expired)
const PILL_STYLES = {
  active:          { bg: "#DCFCE7", fg: "#16A34A", label: "Active" },
  "yet to start":  { bg: "#DBEAFE", fg: "#2563EB", label: "Yet to start" },
  paused:          { bg: "#FEE2E2", fg: "#DC2626", label: "Paused" },
  expired:         { bg: "#E5E7EB", fg: "#4B5563", label: "Expired" },
  published:       { bg: "#DCFCE7", fg: "#16A34A", label: "Published" },
  "not published": { bg: "#FEF3C7", fg: "#B45309", label: "Not published" },
};

// Pass `status`, or the old boolean `active` prop
export function StatusPill({ status, active }) {
  const key = (status || (active ? "active" : "paused")).toLowerCase().trim();
  const s = PILL_STYLES[key] || PILL_STYLES.paused;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.fg }} />
      {s.label}
    </span>
  );
}

export function StatusToggle({ value, onChange }) {
  const btn = (isOn, label, on, off) => (
    <button
      type="button"
      onClick={() => onChange(isOn)}
      className={`flex items-center gap-2 px-4 rounded-lg text-sm font-semibold border transition-all h-8 ${
        value === isOn ? on : off
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${value === isOn ? (isOn ? "bg-green-600" : "bg-red-600") : "bg-gray-300"}`} />
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-3">
      {btn(true, "Active", "bg-green-100 border-green-400 text-green-700", "bg-white border-gray-300 text-gray-400 hover:border-green-300")}
      {btn(false, "Paused", "bg-red-100 border-red-400 text-red-700", "bg-white border-gray-300 text-gray-400 hover:border-red-300")}
    </div>
  );
}

export function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function ModalShell({ title, onClose, onSubmit, submitLabel, submitting, readOnly, children }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} disabled={submitting} className="p-1.5 rounded-md hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        {children}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              onClick={onSubmit}
              disabled={submitting}
              className="px-4 py-2 text-white text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Small confirmation / info dialog.
//  - with `onConfirm`: shows Cancel + confirm buttons
//  - without `onConfirm`: info only, shows a single OK button
export function ConfirmModal({
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  busy = false,
  danger = false,
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100]">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} disabled={busy} className="p-1.5 rounded-md hover:bg-gray-100" aria-label="Close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">{message}</p>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          {onConfirm ? (
            <>
              <button
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                {cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                disabled={busy}
                className={`px-4 py-2 text-white text-sm font-semibold rounded-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 ${
                  danger ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {confirmLabel}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-white text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700"
            >
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Campaign Modal (Add / Edit) ──────────────────────────────────────────────
const EMPTY = { name: "", code: "", locations: [], startDate: "", endDate: "", isActive: true, description: "" };

export function CampaignModal({
  mode,
  initial,
  campaigns,
  locationOptions = [],
  locationsLoading,
  onClose,
  onSave,
}) {
  const { add_campaign, edit_campaign } = useSuperAdmin();

  // Status rules:
  //  - expired      → only End Date can be changed
  //  - everything else (yet to start / active / paused) → fully editable
  const statusKey = (initial?.status || "").toLowerCase().trim();
  const isExpired = mode === "edit" && statusKey === "expired";
  const lock = isExpired;

  // "Yet to start" and "Active" both count as active; only "Paused" is inactive
  const initialActive = initial ? statusKey !== "paused" : true;

  // form.locations holds location IDs
  const [form, setForm] = useState(
    initial
      ? {
          ...EMPTY,
          ...initial,
          isActive: initialActive,
          locations: (initial.locations || []).map((l) => toOpt(l).id),
        }
      : EMPTY
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  // dropdown options as { id, name }; the campaign's saved locations are included
  // even if the location list has not loaded yet
  const allOptions = [
    ...locationOptions.map(toOpt),
    ...(initial?.locations || []).map(toOpt).filter(
      (l) => !locationOptions.some((o) => toOpt(o).id === l.id)
    ),
  ];
  const selectedLocs = allOptions.filter((o) => form.locations.includes(o.id));

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};

    if (!form.endDate) e.endDate = "End date is required";
    else if (form.startDate && form.endDate < form.startDate) e.endDate = "End date must be on or after start date";

    // Expired campaign: only the end date is validated
    if (!lock) {
      const code = (form.code || "").trim().toUpperCase();
      if (!form.name.trim()) e.name = "Campaign name is required";
      if (!/^[A-Z0-9]{4}$/.test(code)) e.code = "Enter exactly 4 letters or digits";
      if (!form.locations.length) e.locations = "Select at least one location";
      if (!form.startDate) e.startDate = "Start date is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Save button: validate first; if an active campaign is being switched to
  // paused, ask for confirmation before saving.
  const handleSubmit = () => {
    if (!validate()) return;
    const deactivating = mode === "edit" && !lock && initialActive && !form.isActive;
    if (deactivating) {
      setConfirmDeactivate(true);
      return;
    }
    save();
  };

  const save = async () => {
    setConfirmDeactivate(false);
    setSubmitting(true);

    const statusChanged = mode === "add" || form.isActive !== initialActive;

    const payload = lock
      ? { end_date: form.endDate } // expired: only the end date can change
      : {
          name: form.name.trim(),
          start_date: form.startDate,
          end_date: form.endDate,
          // status is sent only when it was actually changed, so a "Yet to start"
          // campaign is not forced to "Active" by a plain edit
          ...(statusChanged && { status: form.isActive ? "Active" : "Paused" }),
          description: (form.description || "").trim(),
          locationIds: form.locations,
          ...(mode === "add" && { campaign_id: form.code.trim().toUpperCase() }), // cannot be updated
        };
    const msg = mode === "add" ? "Campaign created successfully" : "Campaign updated successfully";

    try {
      if (!USE_API) {
        toast.success(msg);
        onSave({
          id: initial?.id,
          name: form.name.trim(),
          code: (form.code || "").trim().toUpperCase(),
          locations: selectedLocs,
          startDate: form.startDate,
          endDate: form.endDate,
          isActive: form.isActive,
          description: (form.description || "").trim(),
        });
        onClose();
        return;
      }
      const res = mode === "add" ? await add_campaign(payload) : await edit_campaign(payload, initial.code);
      if (res?.data?.success) {
        toast.success(msg);
        onSave();
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const assetTotal = initial?.totalAssets ?? initial?.counts?.total ?? initial?.assets?.length ?? 0;

  const lockedCls = "disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed";

  return (
    <>
      <ModalShell
        title={mode === "add" ? "Add Campaign" : isExpired ? "Edit Campaign (End date only)" : "Edit Campaign"}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitLabel={mode === "add" ? "Add Campaign" : "Save Changes"}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Campaign Name" required error={errors.name} className="sm:col-span-2">
            <input
              value={form.name}
              disabled={lock}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Diwali"
              className={`${inputCls(errors.name)} ${lockedCls}`}
            />
          </Field>

          <Field label="Campaign Code (4 characters)" required error={errors.code}>
            <input
              value={form.code}
              disabled={mode === "edit"}
              maxLength={4}
              onChange={(e) => set("code", e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
              placeholder="e.g. DW26"
              className={`${inputCls(errors.code)} font-mono ${lockedCls}`}
            />
          </Field>

          <Field label="Status">
            {lock ? (
              <StatusPill status="expired" />
            ) : (
              <StatusToggle value={form.isActive} onChange={(v) => set("isActive", v)} />
            )}
          </Field>

          <div className="sm:col-span-2">
            {/* wrapper blocks interaction when locked (works even if the dropdown has no `disabled` prop) */}
            <div className={lock ? "pointer-events-none opacity-60" : ""} aria-disabled={lock}>
              <MultiSelectDropdown
                label="Locations"
                options={allOptions}
                selected={form.locations}
                onChange={(v) => set("locations", v)}
                placeholder="Select locations"
                error={errors.locations}
                loading={locationsLoading}
                disabled={lock}
              />
            </div>
          </div>

          <Field label="Start Date" required error={errors.startDate}>
            <input
              type="date"
              value={form.startDate}
              disabled={lock}
              // only new campaigns must start today or later; existing ones keep their saved date
              min={minDate}
              onChange={(e) => set("startDate", e.target.value)}
              className={`${inputCls(errors.startDate)} ${lockedCls}`}
            />
          </Field>

          {/* End date stays editable in every status */}
          <Field label="End Date" required error={errors.endDate}>
            <input
              type="date"
              value={form.endDate}
              // expired campaigns can only be extended to today or later
              min={lock ? (form.startDate && form.startDate > minDate ? form.startDate : minDate) : form.startDate || undefined}
              onChange={(e) => set("endDate", e.target.value)}
              className={inputCls(errors.endDate)}
            />
          </Field>

          <Field label="Description" className="sm:col-span-2">
            <textarea
              value={form.description}
              disabled={lock}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Enter campaign description..."
              rows={3}
              className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${lockedCls}`}
            />
          </Field>
        </div>
      </ModalShell>

      {confirmDeactivate && (
        <ConfirmModal
          title="Pause campaign?"
          message={
            assetTotal > 0
              ? `You are changing "${form.name}" from active to paused. Its assets will not be publishable until the campaign is active again. Do you want to continue?`
              : `You are changing "${form.name}" from active to paused. Do you want to continue?`
          }
          confirmLabel="Yes, Pause"
          danger
          busy={submitting}
          onConfirm={save}
          onClose={() => setConfirmDeactivate(false)}
        />
      )}
    </>
  );
}

// ── Asset Modal (Add / Edit) ─────────────────────────────────────────────────
// presetCampaign: set when opened from a campaign's "Add Asset" button

// Options API may return strings or objects, under different keys; normalise to { value, label }
const toChoice = (o) =>
  typeof o === "string"
    ? { value: o, label: o }
    : { value: o.value ?? o.id ?? o.name, label: o.label ?? o.name ?? String(o.value ?? o.id) };

const choicesFrom = (res, key) => {
  const d = res?.data;
  const list = [d?.data, d?.[key], d?.data?.[key], d?.options, d].find(Array.isArray);
  return (list || []).map(toChoice);
};

export function AssetModal({ mode, initial, presetCampaign, campaigns, onClose, onSave }) {
  const { add_asset, edit_asset, fetchAssetOptions } = useSuperAdmin();

  // Campaign is fixed: from preset (add) or from the asset being edited
  const campaign = presetCampaign || campaigns.find((c) => c.id === initial?.campaignId);
  const campaignLocs = (campaign?.locations || []).map(toOpt);

  // Published assets are view-only; only "not published" assets can be edited.
  const readOnly = mode === "edit" && !!initial?.isPublished;

  // Existing assets may arrive as ids or as names; resolve everything to ids
  const initialLocIds = (() => {
    const ids = initial?.locationIds || initial?.location_ids;
    if (Array.isArray(ids)) return ids;

    const names = Array.isArray(initial?.locations)
      ? initial.locations
      : initial?.location
      ? [initial.location]
      : [];
    const fromNames = names
      .map((n) => campaignLocs.find((l) => l.name === (n?.name ?? n))?.id)
      .filter(Boolean);
    if (fromNames.length) return fromNames;

    const single = initial?.locationId || initial?.location_id;
    return single ? [single] : [];
  })();

  const [form, setForm] = useState({
    name: initial?.name || "",
    channel: initial?.channel || "",
    source: initial?.source || "",
    locationIds: initialLocIds, // optional, multiple
    suffix: "", // add mode only: last 3 digits of the asset code (required)
    desc: initial?.desc || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [channels, setChannels] = useState([]);
  const [sources, setSources] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [loadingSources, setLoadingSources] = useState(false);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  // GET /assets/options  → channels (loaded once)
  useEffect(() => {
    if (!USE_API) return;
    let alive = true;
    (async () => {
      setLoadingChannels(true);
      try {
        const res = await fetchAssetOptions();
        if (alive) setChannels(choicesFrom(res, "channels"));
      } catch {
        toast.error("Failed to load channels");
      } finally {
        if (alive) setLoadingChannels(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // GET /assets/options?channel=...  → sources (reloaded when channel changes)
  useEffect(() => {
    if (!USE_API) return;
    if (!form.channel) {
      setSources([]);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingSources(true);
      try {
        const res = await fetchAssetOptions(form.channel);
        if (alive) setSources(choicesFrom(res, "sources"));
      } catch {
        toast.error("Failed to load sources");
      } finally {
        if (alive) setLoadingSources(false);
      }
    })();
    return () => { alive = false; };
  }, [form.channel]);

  const changeChannel = (value) => {
    setForm((p) => ({ ...p, channel: value, source: "" })); // source depends on channel
    setErrors((p) => ({ ...p, channel: "", source: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Asset name is required";
    if (mode === "add") {
      if (!form.suffix) e.suffix = "Asset code is required";
      else if (!/^\d{3}$/.test(form.suffix)) e.suffix = "Enter exactly 3 digits";
    }
    if (!form.channel) e.channel = "Select a channel";
    if (!form.source) e.source = "Select a source";
    // locations are optional
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (readOnly || !validate()) return;
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      channel: form.channel,
      source: form.source,
      // always sent (even empty) so that clearing all locations works on edit
      locationIds: form.locationIds,
      ...((mode === "edit" || form.desc.trim()) && { desc: form.desc.trim() }),
      ...(mode === "add" && {
        campaign_id: campaign.code, // POST /assets
        suffix: form.suffix,        // required: sent as the asset code's last 3 digits
      }),
    };
    const msg = mode === "add" ? "Asset created successfully" : "Asset updated successfully";

    try {
      if (!USE_API) {
        toast.success(msg);
        onSave({
          id: initial?.id,
          campaignId: campaign.id,
          name: payload.name,
          desc: form.desc.trim(),
          channel: form.channel,
          source: form.source,
          locationIds: form.locationIds,
          locations: campaignLocs
            .filter((l) => form.locationIds.includes(l.id))
            .map((l) => l.name),
          location: campaignLocs
            .filter((l) => form.locationIds.includes(l.id))
            .map((l) => l.name)
            .join(", "),
          code: initial?.code || campaign.code + form.suffix,
          status: initial?.status || "not published",
          isPublished: initial ? !!initial.isPublished : false,
        });
        onClose();
        return;
      }
      const res =
        mode === "add" ? await add_asset(payload) : await edit_asset(payload, initial.code); // PATCH /assets/:asset_id
      if (res?.data?.success) {
        toast.success(msg);
        onSave();
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  const baseTitle = readOnly ? "View Asset" : mode === "add" ? "Add Asset" : "Edit Asset";

  return (
    <ModalShell
      title={campaign ? `${baseTitle} (${campaign.name})` : baseTitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={submitting}
      readOnly={readOnly}
      submitLabel={mode === "add" ? "Add Asset" : "Save Changes"}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Row 1: Asset Name + Asset Code */}
        <Field label="Asset Name" required error={errors.name}>
          <input
            value={form.name}
            disabled={readOnly}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Asset 1"
            className={`${inputCls(errors.name)} disabled:bg-gray-100 disabled:text-gray-600`}
          />
        </Field>

        {/* Add: required 3-digit code. Edit: full code shown, never editable. */}
        {mode === "edit" ? (
          <Field label="Asset Code">
            <input
              value={initial?.code || ""}
              disabled
              className={`${inputCls(false)} font-mono bg-gray-100 text-gray-600`}
            />
          </Field>
        ) : (
          <Field label="Asset Code (Suffix)" required error={errors.suffix}>
            <input
              value={form.suffix}
              maxLength={3}
              inputMode="numeric"
              onChange={(e) => set("suffix", e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 001"
              className={`${inputCls(errors.suffix)} font-mono `}
            />
          </Field>
        )}

        <Field label="Channel" required error={errors.channel}>
          <select
            value={form.channel}
            disabled={readOnly || loadingChannels}
            onChange={(e) => changeChannel(e.target.value)}
            className={`${inputCls(errors.channel)} disabled:bg-gray-100 disabled:text-gray-600`}
          >
            <option value="">{loadingChannels ? "Loading channels..." : "Select channel"}</option>
            {channels.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Source" required error={errors.source}>
          <select
            value={form.source}
            disabled={readOnly || !form.channel || loadingSources}
            onChange={(e) => set("source", e.target.value)}
            className={`${inputCls(errors.source)} disabled:bg-gray-100 disabled:text-gray-600`}
          >
            <option value="">
              {!form.channel ? "Select a channel first" : loadingSources ? "Loading sources..." : "Select source"}
            </option>
            {sources.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>

        <div className="sm:col-span-2">
          {readOnly ? (
            <Field label="Locations">
              <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-600 min-h-[38px]">
                {campaignLocs
                  .filter((l) => form.locationIds.includes(l.id))
                  .map((l) => l.name)
                  .join(", ") || "None"}
              </div>
            </Field>
          ) : (
            <MultiSelectDropdown
              label="Locations"
              required={false}
              options={campaignLocs}
              selected={form.locationIds}
              onChange={(v) => set("locationIds", v)}
              placeholder={campaign ? "Select locations" : "No campaign"}
              error={errors.locationIds}
            />
          )}
        </div>

        <Field label="Description" className="sm:col-span-2">
          <textarea
            value={form.desc}
            disabled={readOnly}
            rows={2}
            onChange={(e) => set("desc", e.target.value)}
            placeholder="Enter asset description (optional)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-600"
          />
        </Field>
      </div>
    </ModalShell>
  );
}